import type { Edge, Node } from '@xyflow/react';
import type {
  PushBuilderState,
  SMSBuilderState,
  EmailBuilderState,
  PointBoostBuilderState,
} from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/builder-context';
import type { DutchieTemplate } from 'src/app/queries/email-marketing/dutchie-templates';
import type { MarketingTemplatePreset } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';
import type { EmailDeliverability, SMSDeliverability } from 'src/app/queries/marketing/get-deliverability-metrics';
import type { WorkflowStepStats } from 'src/app/queries/marketing/get-workflow-execution-stats';

export enum NodeType {
  ACTION = 'action',
  ADD_NODE_PLACEHOLDER = 'addNodePlaceholder',
  CONDITIONAL = 'conditional',
  TIME_DELAY = 'timeDelay',
  TRIGGER = 'trigger',
}

export enum NodeLabel {
  ACTION_SEND_EMAIL = 'Send email',
  ACTION_SEND_PUSH = 'Send push notification',
  ACTION_SEND_SMS = 'Send SMS',
  CONDITIONAL_SPLIT = 'Conditional split',
  TIME_DELAY_TIMING = 'Timing',
  TRIGGER_ABANDONED_CART = 'Abandoned cart',
  TRIGGER_AFTER_LOYALTY_ENROLLMENT = 'After loyalty enrollment',
  TRIGGER_CUSTOMER_BIRTHDAY = 'Customer birthday',
  TRIGGER_LOYALTY_POINT_EXPIRATION = 'Loyalty point expiration',
  TRIGGER_NEW_CUSTOMER = 'New customer',
  TRIGGER_SEGMENT_ENTRY = 'Segment entry',
  TRIGGER_UPCOMING_BIRTHDAY = 'Upcoming birthday',
}

export enum TriggerType {
  ABANDONED_CART = 'abandonedCart',
  AFTER_LOYALTY_ENROLLMENT = 'afterLoyaltyEnrollment',
  CUSTOMER_BIRTHDAY = 'customerBirthday',
  LOYALTY_POINT_EXPIRATION = 'loyaltyPointExpiration',
  NEW_CUSTOMER = 'newCustomer',
  SEGMENT_ENTRY = 'segmentEntry',
  UPCOMING_BIRTHDAY = 'upcomingBirthday',
}

export type TimingType = 'delay';
export const timingTypeLabels: Record<TimingType, string> = {
  delay: 'Delay',
};

export type ConditionalType =
  | 'clickedEmail'
  | 'isInSegment'
  | 'madeAPurchase'
  | 'openedEmail'
  | 'openedPush'
  | 'openedSMS';

export const conditionalTypeLabels: Record<ConditionalType, string> = {
  clickedEmail: 'Clicked email',
  isInSegment: 'Is in segment',
  madeAPurchase: 'Made a purchase',
  openedEmail: 'Opened email',
  openedPush: 'Opened push',
  openedSMS: 'Opened SMS',
};

export enum ActionTypeEnum {
  GRANT_POINTS = 'grantPoints',
  SEND_EMAIL = 'sendEmail',
  SEND_PUSH_NOTIFICATION = 'sendPushNotification',
  SEND_SMS = 'sendSMS',
}

export type ActionType = 'grantPoints' | 'sendEmail' | 'sendPushNotification' | 'sendSMS';

export const actionTypeLabels: Record<ActionType, string> = {
  [ActionTypeEnum.SEND_EMAIL]: 'Send email',
  [ActionTypeEnum.SEND_PUSH_NOTIFICATION]: 'Send push notification',
  [ActionTypeEnum.SEND_SMS]: 'Send SMS',
  [ActionTypeEnum.GRANT_POINTS]: 'Grant points',
};

export type BaseNodeData = {
  isDeliverabilityMetricsLoading?: boolean;
  nodeType: NodeType;
  onAddNode?: (sourceNodeId: string, nodeType: NodeType, edgeType?: 'no' | 'yes') => void;
  reportIsValid?: (valid: boolean) => void;
  showAnalytics?: boolean;
  stats?: WorkflowStepStats;
};

export type TriggerNodeData = BaseNodeData & {
  config: object;
  nodeType: NodeType.TRIGGER;
};

export type AddNodePlaceholderNodeData = BaseNodeData & {
  nodeType: NodeType.ADD_NODE_PLACEHOLDER;
};

export type ActionNodeData = BaseNodeData & {
  actionType: ActionType;
  config: {
    emailConfig: EmailBuilderState | null;
    pointBoostConfig: PointBoostBuilderState | null;
    pushConfig: PushBuilderState | null;
    savedTemplateId: number | null;
    selectedTemplate?: DutchieTemplate | null;
    selectedTemplatePreset?: MarketingTemplatePreset | null;
    smsConfig: SMSBuilderState | null;
  };
  deliverabilityStats?: EmailDeliverability | SMSDeliverability | null;
  nodeType: NodeType.ACTION;
};

export type TimingNodeData = BaseNodeData & {
  config: {
    durationInMinutes: number | null;
    timeOfDayToSend: string | null;
    timeUnitId: number | null;
  };
  nodeType: NodeType.TIME_DELAY;
};

export type ConditionalNodeData = BaseNodeData & {
  conditionalType: ConditionalType;
  config: {
    conditionalReferenceNodeId: number | null;
    durationInMinutes: number | null;
    segmentIds: number[] | null;
    timeUnitId: number | null;
  };
  nodeType: NodeType.CONDITIONAL;
};

export type TriggerNode = Node<TriggerNodeData>;
export type ActionNode = Node<ActionNodeData>;
export type TimingNode = Node<TimingNodeData>;
export type ConditionalNode = Node<ConditionalNodeData>;
export type AddNodePlaceholderNode = Node<AddNodePlaceholderNodeData>;
export type WorkflowCampaignNode = ActionNode | ConditionalNode | TimingNode | TriggerNode;

export type WorkflowCampaignEdge = Edge & {
  edgeType: 'next' | 'no' | 'yes';
};

// Node type definitions with their labels and configurations
export const nodeTypes: Record<NodeType, { description: string; label: string; subtypes?: Record<string, string> }> = {
  [NodeType.TRIGGER]: {
    label: 'Trigger',
    description: 'Create triggers to start a workflow',
    subtypes: {
      [TriggerType.AFTER_LOYALTY_ENROLLMENT]: NodeLabel.TRIGGER_AFTER_LOYALTY_ENROLLMENT,
      [TriggerType.NEW_CUSTOMER]: NodeLabel.TRIGGER_NEW_CUSTOMER,
      [TriggerType.UPCOMING_BIRTHDAY]: NodeLabel.TRIGGER_UPCOMING_BIRTHDAY,
      [TriggerType.ABANDONED_CART]: NodeLabel.TRIGGER_ABANDONED_CART,
      [TriggerType.LOYALTY_POINT_EXPIRATION]: NodeLabel.TRIGGER_LOYALTY_POINT_EXPIRATION,
      [TriggerType.SEGMENT_ENTRY]: NodeLabel.TRIGGER_SEGMENT_ENTRY,
    },
  },
  [NodeType.ACTION]: {
    label: 'Action',
    description: 'Select the action that you would like to message your audience',
    subtypes: {
      sendEmail: NodeLabel.ACTION_SEND_EMAIL,
      sendSMS: NodeLabel.ACTION_SEND_SMS,
      sendPushNotification: NodeLabel.ACTION_SEND_PUSH,
    },
  },
  [NodeType.TIME_DELAY]: {
    label: 'Time Delay',
    description: 'Set a delay and scheduled messaging',
  },
  [NodeType.CONDITIONAL]: {
    label: 'Conditional',
    description: 'Decision point in workflow that segments based on criteria',
    subtypes: {
      openedUnopened: 'Opened/Unopened',
      clickedNotClicked: 'Clicked/Not clicked',
      madeAPurchase: 'Made a purchase',
      didNotMakeAPurchase: 'Did not make a purchase',
    },
  },
  [NodeType.ADD_NODE_PLACEHOLDER]: {
    label: 'Node placeholder',
    description: 'Add a node to the workflow',
  },
};
